import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import {
  Author,
  Bibliography,
  Multipleprint,
  MultipleprintInBibliography,
} from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import MultipleprintInBibliographyDrawer from "./drawer/MultipleprintInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  multipleprint: Multipleprint;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

/**
 * MultipleprintInBibliography component
 * @returns {JSX.Element}
 */
const MultipleprintInBibliographyLinks = ({
  id,
  multipleprint,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleprintInBibliography",
    type,
  );

  const { mutate: deleteMultipleprintInBibliography } = useDeleteComplexLink(
    id,
    "multipleprintInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteMultipleprintInBibliography({
        id,
        relation: "multipleprintInBibliography",
      });
    },
    [deleteMultipleprintInBibliography],
  );

  const columns: ColumnDef<MultipleprintInBibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliography.bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "bibliography.bibliographieDescriptionAuteur",
        header: t("bibliographieDescriptionAuteur"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey:
          "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
        header: t("bibliographieDansEstampesMultiplesDescriptionReproduitPage"),
      },
      {
        accessorKey: "bibliographieDansEstampesMultiplesDescriptionCitePage",
        header: t("bibliographieDansEstampesMultiplesDescriptionCitePage"),
      },
      {
        accessorKey: "bibliographieDansEstampesMultiplesDescriptionPhotographe",
        header: t("bibliographieDansEstampesMultiplesDescriptionPhotographe"),
      },
      {
        accessorKey:
          "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
        header: t(
          "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
        ),
      },
      {
        accessorKey:
          "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
        header: t(
          "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
        ),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<MultipleprintInBibliography> }) => {
          const data = row.original as MultipleprintInBibliography;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <MultipleprintInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "bibliography" && (
                <ShowElements
                  type="multipleprint"
                  id={row.original.multipleprint.id}
                  elements={[
                    {
                      label: t(
                        "estampesMultiplesDescriptionNumeroEnregistrement",
                      ),
                      value:
                        row.original.multipleprint
                          .estampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("estampesMultiplesDescriptionResume"),
                      value:
                        row.original.multipleprint
                          .estampesMultiplesDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.multipleprint.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "multipleprint" && (
                <ShowElements
                  type="bibliography"
                  id={row.original.bibliography.id}
                  elements={[
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        row.original.bibliography
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text",
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value:
                        row.original.bibliography
                          .bibliographieDescriptionResumeLong,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.bibliography.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">{t("linkedBibliographies")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <MultipleprintToBibliographyLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        multipleprint={multipleprint}
        isAddMode={isAddMode}
        columnsLink={columns}
        searchContent={searchContent}
      />
    </div>
  );
};

type MultipleprintInBibliographyLinksProps =
  LinksTableProps<MultipleprintInBibliography> & {
    multipleprint: Multipleprint | undefined;
  };

//Composant pour Oeuvres
const MultipleprintToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  multipleprint,
  isAddMode,
  columnsLink,
  searchContent,
}: MultipleprintInBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionAnnee}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.relatedAuthor
                  .map((author: Author) => author.artistesAuteursDescriptionNom)
                  .join(", ")}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Bibliography;
          if (!multipleprint) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="bibliography"
                id={row.original.id}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value:
                      row.original.bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value: row.original.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <MultipleprintInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={id}
                type={"multipleprint"}
                data={{
                  id: "",
                  bibliography: data,
                  multipleprint: multipleprint,
                  bibliographieDansEstampesMultiplesDescriptionReproduitPage:
                    "",
                  bibliographieDansEstampesMultiplesDescriptionCitePage: "",
                  bibliographieDansEstampesMultiplesDescriptionPhotographe: "",
                  bibliographieDansEstampesMultiplesDescriptionReproductionCoul:
                    false,
                  bibliographieDansEstampesMultiplesDescriptionExemplaireRepr:
                    "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [fields, id, multipleprint, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsBibliographies}
          data={notRelatedBibliographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedBibliographies?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsBibliographies,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedBibliographies?.data,
      notRelatedBibliographies?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

export default MultipleprintInBibliographyLinks;
